import React, { useState, useEffect } from "react";
import ProgressStepper from "./components/ProgressStepper";
import Step0 from "./components/steps/Step0";
import Step1 from "./components/steps/Step1";
import Step2 from "./components/steps/Step2";
import Step3 from "./components/steps/Step3";
import Step4 from "./components/steps/Step4";
import Step5 from "./components/steps/Step5";
import Step6 from "./components/steps/Step6";
import Step7 from "./components/steps/Step7";
import Step8 from "./components/steps/Step8";
import Step9 from "./components/steps/Step9";
import Step10 from "./components/steps/Step10";
import Step11 from "./components/steps/Step11";
import Step12 from "./components/steps/Step12";
import Step13 from "./components/steps/Step13";
import CreateAccount from "./components/auth/createAccount";
import LoginAccount from "./components/auth/loginAccount";
import Progress from "./components/steps/Progress";
import BackgroundImages from "./components/shared/BackgroundImages";
import { initTracking } from "./utils/tracking";
import ForgotPassword from "./components/auth/forgotPassword";
import Footer from "./components/Footer";
import TagManager from "react-gtm-module";
import { trackQuizStep } from "./utils/analytics";

const QUIZ_STEPS = [
  "welcome",
  "age",
  "promotionAge",
  "useAI",
  "friendAI",
  "promotionAI",
  "feel",
  "frustration",
  "quality",
  "treat",
  "communication",
  "promotionCommunication",
  "gender",
  "typeOfLook",
  "loading",
  "createAccount",
  "login",
  "forgotPassword",
];

// Quiz question screens in display order. Their position in this list (1-based)
// is the GTM step number — Step0 (welcome) is step_1, age is step_2, etc. The
// promotion/result screens (2, 5, 11) and the loading/auth screens are excluded
// so only the 11 quiz questions fire step_1 … step_11.
const QUIZ_QUESTION_STEPS = [0, 1, 3, 4, 6, 7, 8, 9, 10, 12, 13];

// Steps that should not show progress stepper and not be counted in total
const HIDDEN_STEPS = [0, 14, 15, 16, 17]; // Step0, loading, createAccount, login

// Steps where both girls should be hidden completely
const HIDDEN_STEPS_GIRLS = [0, 2, 3, 5, 8, 11, 13, 14, 15, 16, 17];

function App() {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showLogin, setShowLogin] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);

  useEffect(() => {
    TagManager.initialize({ gtmId: "GTM-N8LQSVFS" });
  }, []);

  useEffect(() => {
    initTracking();
  }, []);

  // Fire step_1 … step_11 as each quiz question screen is shown (not on answer).
  useEffect(() => {
    const stepIndex = QUIZ_QUESTION_STEPS.indexOf(currentStep);
    if (stepIndex !== -1) {
      trackQuizStep(stepIndex + 1, QUIZ_STEPS[currentStep]);
    }
  }, [currentStep]);

  const handleAnswer = (step, answer) => {
    if (isTransitioning) return;
    setIsTransitioning(true);

    setAnswers((prev) => ({
      ...prev,
      [step]: answer,
    }));

    setTimeout(() => {
      setCurrentStep((prev) => prev + 1);
      setTimeout(() => {
        setIsTransitioning(false);
      }, 100);
    }, 500);
  };

  const handleContinue = () => {
    if (isTransitioning) return;

    setIsTransitioning(true);

    setTimeout(() => {
      setCurrentStep((prev) => prev + 1);
      setTimeout(() => {
        setIsTransitioning(false);
      }, 100);
    }, 100);
  };

  const handleAccountFlow = () => {
    if (isTransitioning) return;

    setIsTransitioning(true);

    if (showLogin) {
      setShowLogin(false);
      setCurrentStep(16);
    } else {
      setShowLogin(true);
      setCurrentStep(15);
    }

    setTimeout(() => {
      setIsTransitioning(false);
    }, 100);
  };

  const goToCreate = () => {
    if (isTransitioning) return;

    setIsTransitioning(true);
    setShowLogin(false);
    setCurrentStep(15);

    setTimeout(() => {
      setIsTransitioning(false);
    }, 100);
  };

  const goToLogin = () => {
    if (isTransitioning) return;

    setIsTransitioning(true);
    setShowLogin(true);
    setCurrentStep(16);

    setTimeout(() => {
      setIsTransitioning(false);
    }, 100);
  };

  const goToForgotPassword = () => {
    if (isTransitioning) return;

    setIsTransitioning(true);
    setShowLogin(false);
    setCurrentStep(17);

    setTimeout(() => {
      setIsTransitioning(false);
    }, 100);
  };

  const backToLogIn = () => {
    if (isTransitioning) return;

    setIsTransitioning(true);
    setShowLogin(true);
    setCurrentStep(16);

    setTimeout(() => {
      setIsTransitioning(false);
    }, 100);
  };

  const resetQuiz = () => {
    if (isTransitioning) return;

    setCurrentStep(0);
    setAnswers({});
    setShowLogin(false);
    setIsTransitioning(false);
  };

  const getVisibleSteps = () => {
    return QUIZ_STEPS.filter((_, index) => !HIDDEN_STEPS.includes(index));
  };

  const shouldShowProgressStepper = () => {
    return !HIDDEN_STEPS.includes(currentStep);
  };

  const getVisibleGirls = () => {
    return QUIZ_STEPS.filter((_, index) => !HIDDEN_STEPS_GIRLS.includes(index));
  };

  const shouldShowGirls = () => {
    return !HIDDEN_STEPS_GIRLS.includes(currentStep);
  };

  const getGirlVisibility = () => {
    const currentStepIndex = currentStep;

    if (HIDDEN_STEPS_GIRLS.includes(currentStepIndex)) {
      return { showBlond: false, showBrown: false };
    }

    if (currentStepIndex === 15) {
      return { showBlond: true, showBrown: true };
    }

    if ([1, 4, 6, 9, 10, 12].includes(currentStepIndex)) {
      return { showBlond: true, showBrown: true };
    }

    return { showBlond: false, showBrown: false };
  };

  const renderCurrentSlide = () => {
    switch (currentStep) {
      case 0:
        return <Step0 onAnswer={handleAnswer} isDisabled={isTransitioning} />;
      case 1:
        return <Step1 onAnswer={handleAnswer} isDisabled={isTransitioning} />;
      case 2:
        return (
          <Step2
            onContinue={handleContinue}
            answers={answers}
            isDisabled={isTransitioning}
          />
        );
      case 3:
        return <Step3 onAnswer={handleAnswer} isDisabled={isTransitioning} />;
      case 4:
        return <Step4 onAnswer={handleAnswer} isDisabled={isTransitioning} />;
      case 5:
        return (
          <Step5 onContinue={handleContinue} isDisabled={isTransitioning} />
        );
      case 6:
        return <Step6 onAnswer={handleAnswer} isDisabled={isTransitioning} />;
      case 7:
        return <Step7 onAnswer={handleAnswer} isDisabled={isTransitioning} />;
      case 8:
        return <Step8 onAnswer={handleAnswer} isDisabled={isTransitioning} />;
      case 9:
        return <Step9 onAnswer={handleAnswer} isDisabled={isTransitioning} />;
      case 10:
        return <Step10 onAnswer={handleAnswer} isDisabled={isTransitioning} />;
      case 11:
        return (
          <Step11 onContinue={handleContinue} isDisabled={isTransitioning} />
        );
      case 12:
        return <Step12 onAnswer={handleAnswer} isDisabled={isTransitioning} />;
      case 13:
        return <Step13 onAnswer={handleAnswer} isDisabled={isTransitioning} />;
      case 14:
        return (
          <Progress
            onContinue={() => setCurrentStep(15)}
            isDisabled={isTransitioning}
          />
        );
      case 15:
        return (
          <CreateAccount onGoToLogin={goToLogin} isDisabled={isTransitioning} />
        );
      case 16:
        return (
          <LoginAccount
            onGoToCreate={goToCreate}
            onForgotPassword={goToForgotPassword}
            isDisabled={isTransitioning}
          />
        );
      case 17:
        return (
          <ForgotPassword
            onBack={backToLogIn}
            onContinue={handleAccountFlow}
            isDisabled={isTransitioning}
          />
        );
      default:
        return (
          <div>
            <div>Quiz completed!</div>
            <button onClick={resetQuiz} disabled={isTransitioning}>
              Restart Quiz
            </button>
          </div>
        );
    }
  };

  return (
    <>
      <div
        className={`quiz-container ${isTransitioning ? "transitioning" : ""}`}
        data-step={currentStep}
      >
        {shouldShowGirls() && (
          <BackgroundImages
            blondGirlClass="blondGirl girlBlondMobile"
            brownGirlClass="brownGirl girlBrownMobile"
            showBlondGirl={getGirlVisibility().showBlond}
            showBrownGirl={getGirlVisibility().showBrown}
            totalSteps={getVisibleGirls().length}
            visibleSteps={getVisibleGirls()}
          />
        )}

        {shouldShowProgressStepper() && (
          <ProgressStepper
            currentStep={currentStep}
            totalSteps={getVisibleSteps().length}
            visibleSteps={getVisibleSteps()}
          />
        )}
        {renderCurrentSlide()}
      </div>
      <Footer />
    </>
  );
}

export default App;
